import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Smartphone, MessageCircle, ChevronLeft } from 'lucide-react'
import { SmsDeviceSetupContent } from './GenerateApiKey'
import { WhatsAppDeviceSetupContent } from './WhatsAppDeviceModal'

type GatewayType = null | 'sms' | 'whatsapp'

interface AddDeviceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial?: GatewayType
}

export function AddDeviceModal({ open, onOpenChange, initial = null }: AddDeviceModalProps) {
  const [selected, setSelected] = useState<GatewayType>(initial)

  const handleClose = () => {
    onOpenChange(false)
    setTimeout(() => setSelected(null), 300)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {selected && (
              <button
                onClick={() => setSelected(null)}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                aria-label="Go back"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <DialogTitle>
              {!selected && 'Add a sender'}
              {selected === 'sms' && 'Add an Android phone'}
              {selected === 'whatsapp' && 'Add a WhatsApp number'}
            </DialogTitle>
          </div>
          <DialogDescription>
            {!selected && 'Choose what you want to send from.'}
            {selected === 'sms' && 'Scan the QR code with the TextRedge Android app.'}
            {selected === 'whatsapp' && 'Link your WhatsApp account to send from it.'}
          </DialogDescription>
        </DialogHeader>

        {!selected && (
          <div className="grid grid-cols-2 gap-4 py-4">
            <button
              onClick={() => setSelected('sms')}
              className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
            >
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Smartphone className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">SMS Gateway</p>
                <p className="text-xs text-gray-500 mt-0.5">Android device via TextRedge app</p>
              </div>
            </button>

            <button
              onClick={() => setSelected('whatsapp')}
              className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-green-200 hover:border-green-500 hover:bg-green-50 transition-colors text-center"
            >
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 text-sm">WhatsApp Gateway</p>
                <p className="text-xs text-gray-500 mt-0.5">Send via your WhatsApp account</p>
              </div>
            </button>
          </div>
        )}

        {selected === 'sms' && <SmsDeviceSetupContent onDone={handleClose} />}
        {selected === 'whatsapp' && <WhatsAppDeviceSetupContent onDone={handleClose} />}
      </DialogContent>
    </Dialog>
  )
}
